// Core Modules
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';

// Components
import { EmptyStateComponent } from '@shared/components/empty-state/empty-state.component';

// Services
import { AuthService } from '@core/services/auth/auth.service';
import { SessionManagerService } from '@core/services/session-manager/session-manager.service';

// Enums
import { UserProfileFieldsEnum } from '@shared/enum/user-profile-fields.enum';

/**
 * Shown when a signed-in user's scopes admit no navigation destination at all. Rendered without the
 * app shell — an empty nav rail around an error page reads as a broken application — so Sign out is
 * the only way off it.
 */
@Component({
  selector: 'app-no-access',
  templateUrl: './no-access.component.html',
  imports: [EmptyStateComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NoAccessComponent {
  // Injectors
  private readonly authService = inject(AuthService);
  private readonly sessionManager = inject(SessionManagerService);

  // Public Members
  /** Signed-in email, read from the stored profile so it survives a browser refresh. */
  public readonly email: string = this.authService.getUserProfileBasedOnFields([UserProfileFieldsEnum.EMAIL])?.email || '';

  /** Names the account when we know it, so a screenshot is enough for support to act on. */
  public readonly message: string = this.email
    ? `${this.email} has no permissions for this application. Contact your administrator to request access.`
    : 'Your account has no permissions for this application. Contact your administrator to request access.';

  /** End the session and return to the login screen. */
  public signOut(): void {
    this.sessionManager.stopWatching();
    this.authService.logout();
  }
}
